import {
  AfterViewInit,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import type { SdTabGroup } from '@solid-design-system/components';
import { fondsportraitTabQueryParamName } from 'src/environments/environment';

@Directive({ selector: '[wctActiveTab]', standalone: true, exportAs: 'activeTabs' })
export class FondsportraitActiveTabDirective implements AfterViewInit, OnDestroy {
  private readonly tabGroup: ElementRef<SdTabGroup> = inject(ElementRef);

  public readonly initialTab: InputSignal<string> = input.required();

  public readonly activeTab: WritableSignal<string | null> = signal(null);

  constructor() {
    effect(this.updateActiveTabUrlSearchParam);
    window.addEventListener('popstate', this.handleNavigationChange);
  }

  ngAfterViewInit(): void {
    this.setInitialTabFromSearchParam();
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.handleNavigationChange);
  }

  public handleTabShowEvent(event: Event): void {
    const name: unknown = (event as CustomEvent).detail?.name;

    if (typeof name === 'string') {
      this.activeTab.set(name);
    }
  }

  private readonly handleNavigationChange = (): void => {
    const urlActiveTab: string = this.getActiveTabFromSearchParams() || this.initialTab();
    this.activeTab.set(urlActiveTab);
    this.tabGroup.nativeElement.show(urlActiveTab);
  };

  private setInitialTabFromSearchParam(): void {
    const detectedInitialTab: string | null = this.getActiveTabFromSearchParams();

    if (!detectedInitialTab) {
      this.activeTab.set(this.initialTab());
      return;
    }

    this.activeTab.set(detectedInitialTab);

    setTimeout(() => {
      this.tabGroup.nativeElement.show(detectedInitialTab);
    });
  }

  private readonly updateActiveTabUrlSearchParam = (): void => {
    const activeTab: string | null = this.activeTab();

    if (!activeTab) {
      return;
    }

    const url = new URL(window.location.href);
    const currentUrlActiveTab: string | null = this.getActiveTabFromSearchParams();
    const isInitialLoadingOfFondsportrait: boolean = activeTab === this.initialTab() && currentUrlActiveTab === null;

    if (currentUrlActiveTab === activeTab || isInitialLoadingOfFondsportrait) {
      return;
    }

    url.searchParams.set(fondsportraitTabQueryParamName, activeTab);
    window.history.pushState(null, '', url.toString());
  };

  private getActiveTabFromSearchParams(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(fondsportraitTabQueryParamName);
  }
}
